import VueRouter, { NavigationGuard, Route } from 'vue-router';
import { magic } from '@gig/magic-sdk';
import {
    getWelcomeNotificationKey,
    getWelcomeNotificationParams,
    getWelcomeNotificationOptions,
} from '@gig/magic-jurisdictions/dist/util/welcome-notification';
import { notificationService } from '@gig/magic-messaging';
import { translate as $t } from '@gig/magic-common';
import { IUserProfile } from '@gig/magic-sdk/dist/adapter/users/user';
import { Jurisdiction } from '@gig/magic-jurisdictions';
import { getUserJurisdiction } from '../helpers/jurisdiction.helpers';
import { ACCOUNT_REQUIRES_VERIFICATION_QUERY } from '@gig/magic-messages';

interface INotificationState {
    hasShown: boolean;
    sessionId: string | null;
}

const welcomeNotificationState: INotificationState = {
    hasShown: false,
    sessionId: null,
};

export function initDGOJWelcomeNotificationGuard(router: VueRouter) {
    const welcomeNotificationGuard: NavigationGuard = async (
        route: Route,
        _,
        next,
    ) => {
        const user = magic.session.getUser();

        if (user?.sessionId !== welcomeNotificationState.sessionId) {
            welcomeNotificationState.hasShown = false;
            welcomeNotificationState.sessionId = user?.sessionId || null;
        }

        if (welcomeNotificationState.hasShown || !user) {
            next();
            return;
        }

        if (!(await isDGOJ())) {
            return next();
        }

        const requiresVerification = await window.queryBus.query<boolean>(
            ACCOUNT_REQUIRES_VERIFICATION_QUERY,
        );

        if (requiresVerification) {
            return next();
        }

        const jurisdiction = await getUserJurisdiction();

        const { lastLogins } = (await magic.invokeCached(
            60,
            magic.methods.users,
            'getUserProfile',
        )()) as IUserProfile;

        const mappedLogins = lastLogins.map((login) => ({
            ...login,
            dateCreated: login.lastLoginOn,
        }));

        const notificationKey = getWelcomeNotificationKey(
            jurisdiction,
            mappedLogins,
        );
        const notificationParams = getWelcomeNotificationParams(
            jurisdiction,
            mappedLogins,
        );
        const notificationOptions = getWelcomeNotificationOptions(
            jurisdiction,
            mappedLogins,
        );

        notificationService.show($t(notificationKey, notificationParams), {
            uniqueID: 'login-welcome-notification',
            ...notificationOptions,
        });

        welcomeNotificationState.hasShown = true;
        next();
    };

    magic.events.sessionDestroyed(() => {
        welcomeNotificationState.hasShown = false;
        welcomeNotificationState.sessionId = null;
    });

    router.beforeEach(welcomeNotificationGuard);
}

async function isDGOJ(): Promise<boolean> {
    return (await getUserJurisdiction()) === Jurisdiction.DGOJ;
}
